<template>
  <div class="modal-wrapper">
    <div class="main-column">
      <div class="updates-header">
        <h4>{{ $t('notifications') }}</h4>
        <div class="close" @click="$emit('close')">
          <img src="@/assets/_desktop/_sidebar/menu-collapse.svg" />
        </div>
      </div>
      <calendar-bell-popup-alert :overdue-status="overdueStatus" />
      <calendar-bell-popup-by-date v-for="update in notifications" :key="update[0]" :update-name="update[0]" :update-list-prop="update[1]" />
      <transition name="fade">
        <div v-if="isNotificationsPanelEmpty" class="placeholder">
          <img src="@/assets/illustrations/sloth-sleeping-gray-blue.svg" alt="Sleeping sloth" />
          <div class="text">{{ $t('noNotifications') }}</div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside'
import Color from '@/components/Color'
import Constants from '@/components/Constants'
import ParseFunctions from '@/components/ParseFunctions'
import CalendarBellPopupByDate from './CalendarBellPopupByDate.vue'
import CalendarBellPopupAlert from './CalendarBellPopupAlert.vue'
import EventBus from '@/EventBus'

export default {
  name: 'CalendarBellPopup',

  components: {
    CalendarBellPopupByDate,
    CalendarBellPopupAlert,
  },

  directives: {
    clickOutside: vClickOutside.directive,
  },

  mixins: [Color, Constants, ParseFunctions],

  props: {
    teamId: {
      type: String,
      default: '',
    },
    calendarDate: {
      type: Date,
      default: () => new Date(),
    },
    overdueStatus: {
      type: Object,
      default: () => {
        userTeamWeekStatus: 'ok'
      },
    },
  },

  data() {
    return {
      notifsLoaded: false,
      notifications: [],
    }
  },

  computed: {
    isNotificationsPanelEmpty() {
      const noAlertsShown =
        this.overdueStatus.userTeamWeekStatus === 'ok' &&
        !this.overdueStatus.deadlineChangedToDay &&
        (!this.overdueStatus.teamNotFilled || this.overdueStatus.teamNotFilled.length === 0)
      return this.notifsLoaded & noAlertsShown && this.notifications.length === 0
    },
  },

  async created() {
    await this.load(this.teamId)
  },
  mounted() {
    EventBus.$on('team-calendar-selected', this.teamCalendarSelected)
    window.addEventListener('focus', this.onFocus)
  },
  destroyed() {
    EventBus.$off('team-calendar-selected', this.teamCalendarSelected)
    window.removeEventListener('focus', this.onFocus)
  },

  methods: {
    teamCalendarSelected(params) {
      this.load(params.teamId)
    },
    mergeNotifications(oldNotifications, newNotifications, limit) {
      if (oldNotifications) {
        if (newNotifications && newNotifications.length > 0 && newNotifications[0].length > 0 && newNotifications[0][1].length > 0) {
          newNotifications[0][1] = [...newNotifications[0][1], ...oldNotifications[0][1]].slice(0, limit)
        } else {
          newNotifications = oldNotifications
        }
      }
      return newNotifications
    },
    async load(teamId) {
      this.notifsLoaded = false

      // check if team's notifications are stored
      if (this.$store.state.bufferedTeamsNotifications[teamId]) {
        // if YES: update the notifications variable right away
        this.notifications = this.$store.state.bufferedTeamsNotifications[teamId].notifications
        this.notifsLoaded = true
      }

      let userSettings = this.$store.state.userSettings
      if (!userSettings) {
        let res = await this.userSettingsCLAPI()
        if (res.status === 'ok') {
          userSettings = res.settings
          this.$store.commit('setUserSettings', res.settings)
        } else {
          this.showServerError(res)
          return
        }
      }
      if (userSettings) {
        let minDate
        let oldNotifications
        if (this.$store.state.bufferedTeamsNotifications[teamId]) {
          minDate = this.$store.state.bufferedTeamsNotifications[teamId].lastRecordDate
          oldNotifications = this.$store.state.bufferedTeamsNotifications[teamId].notifications
        }
        this.getTeamUpdates(teamId, userSettings, minDate).then((res) => {
          let limit = 50
          if (res.status == 'ok') {
            let notificationsMerged = this.mergeNotifications(oldNotifications, res.notifications, limit)
            if (this.teamId == teamId) {
              this.notifications = notificationsMerged
              this.notifsLoaded = true
            }
            let lastRecordDate = res.lastRecordDate

            this.$store.commit('updateBufferedTeamsNotifications', { key: teamId, value: { notifications: notificationsMerged, lastRecordDate } })
          } else {
            this.showServerError(res)
          }
        })
      }
    },

    onFocus() {
      this.load(this.teamId)
    },

    deleteEntry(idx) {
      for (let entriesInRange of this.notifications) {
        for (let entry of entriesInRange[1]) {
          if (entry.id == idx) {
            entriesInRange[1] = entriesInRange[1].filter(function (el) {
              return el.id !== idx
            })
          }
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.modal-wrapper {
  z-index: 5;
  border: 1px solid #eceff2;
  border-top: 0;
  border-bottom: 0;
  background-color: white;
  width: 300px;
  // position: fixed;
  height: 100%;
  align-self: flex-end;
  //overflow-y: scroll;
  overflow-x: hidden;

  .main-column {
    font-size: 14px;
    position: relative;
    padding: 0 20px 24px 20px;
    line-height: 20px;
    width: 300px;
    @include respond-below(sm) {
      width: 100%;
    }

    .placeholder {
      position: absolute;
      top: 88px;
      left: 0;
      right: 0;
      text-align: center;
      color: $bluebell;
      img {
        width: min(400px, 90%);
      }
      .text {
        @include font-rule('averta-bold');
        font-size: 16px;
        margin-top: 32px;
      }
    }
  }

  .updates-header {
    // margin-bottom: 12px;
    padding: 24px 0 20px 0;
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: white;
    display: flex;
    justify-content: space-between;
    .close {
      cursor: pointer;
      width: 40px;
      height: 40px;
      margin-top: -12px;
      transition: background-color 0.15s ease-in-out;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: rotate(180deg);
      &:hover {
        background-color: $white-lilac;
      }
      @include respond-below(sm) {
        img {
          display: none;
        }
        background: url('~@/assets/_desktop/_calendars/Close.svg') no-repeat center center;
      }
    }
  }

  .update-header {
    font-family: 'averta-bold';
    color: black;
    font-size: 16px;
    line-height: 16px;
    width: 100%;
  }
}
</style>
